/**
 * storage.ts — Helpers de persistencia para mocks con localStorage.
 * Fuente única: cualquier handler importa desde aquí.
 *
 * COMPRESIÓN TRANSPARENTE:
 *  - Los valores JSON grandes (> ~32 KB serializados) se comprimen con lz-string
 *    (UTF-16, síncrono) y se guardan con el prefijo `lz1:`. La lectura los
 *    descomprime automáticamente. Los valores pequeños se guardan planos (JSON)
 *    para no penalizar el rendimiento de lecturas frecuentes.
 *  - Los lectores de negocio deben usar `getStorage`/`txRead` (que descomprimen).
 *    Las claves escalares crudas (setRaw/getRaw) NUNCA se comprimen.
 *  - Compatible con el espejo IndexedDB y la restauración: ambos tratan la
 *    cadena como opaca (comprimida o plana) y getStorage maneja ambas.
 *
 * REGLA CRÍTICA (anti pérdida de datos): si una escritura falla por cuota llena
 * (QuotaExceededError), NUNCA se descarta en silencio. Se intenta liberar espacio
 * regenerable (blobs base64 de OTRAS claves: logos/imágenes que se pueden volver a
 * subir) y se reintenta. Si aun así falla, se emite el evento `altenot:storage-quota`
 * y se LANZA `StorageQuotaError` para que el flujo que intentó escribir muestre un
 * error honesto al usuario en vez de celebrar una operación que no se guardó.
 */

import { logger } from "@/lib/logger";
import * as lzString from "lz-string";
import { StorageQuotaError } from "@/lib/persistence/storageQuotaError";

export const STORAGE_QUOTA_EVENT = "altenot:storage-quota";

/** Prefijo de valores comprimidos en localStorage. No colisiona con JSON plano. */
const LZ_PREFIX = "lz1:";
/** Umbral (longitud del JSON serializado) a partir del cual se comprime. */
const COMPRESS_THRESHOLD_CHARS = 32_000;

/** Serializa un valor para persistir: plano o comprimido (si vale la pena). */
export function serializeForStorage(val: unknown): string {
  const json = JSON.stringify(val);
  if (json.length > COMPRESS_THRESHOLD_CHARS) {
    const compressed = lzString.compressToUTF16(json);
    // Solo comprimir si de verdad reduce el tamaño.
    if (compressed.length * 2 < json.length) {
      return LZ_PREFIX + compressed;
    }
  }
  return json;
}

/** Parsea un valor persistido: maneja tanto plano como comprimido. */
export function parsePersistedValue(stored: string): unknown {
  if (stored.startsWith(LZ_PREFIX)) {
    const json = lzString.decompressFromUTF16(stored.slice(LZ_PREFIX.length));
    if (json === null) throw new Error("lz decompress falló");
    return JSON.parse(json);
  }
  return JSON.parse(stored);
}

/** @deprecated Use txRead/txWrite from @/lib/persistence/transactionalStore instead. */
export const getStorage = <T>(key: string, defaultVal: T): T => {
  try {
    const stored = localStorage.getItem(key);
    return stored !== null ? (parsePersistedValue(stored) as T) : defaultVal;
  } catch {
    return defaultVal;
  }
};

function isQuotaError(e: unknown): boolean {
  if (e instanceof DOMException) {
    return (
      e.name === "QuotaExceededError" ||
      e.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
      e.code === 22 ||
      e.code === 1014
    );
  }
  // Algunos WebView lanzan un Error genérico con el nombre en el mensaje.
  return e instanceof Error && /quota/i.test(e.message);
}

function notifyQuotaIssue(key: string): void {
  try {
    window.dispatchEvent(
      new CustomEvent(STORAGE_QUOTA_EVENT, { detail: { key } }),
    );
  } catch {
    /* entorno sin window (tests): ignorar */
  }
}

/** Quita campos base64 (`data:`) de un objeto plano (versión ligera). */
function stripBase64FromObject(
  val: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(val).filter(
      ([, v]) => typeof v !== "string" || !(v as string).startsWith("data:"),
    ),
  );
}

/**
 * Último recurso ante cuota llena: elimina SOLO blobs base64 grandes guardados
 * bajo OTRAS claves (logos/imágenes regenerables). Nunca toca la clave que se
 * está escribiendo ni arrays transaccionales (ventas/cierres/movimientos).
 * @returns número de claves liberadas.
 */
function evictRegenerableBlobs(skipKey: string): number {
  let evicted = 0;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k) keys.push(k);
    }
    for (const k of keys) {
      if (k === skipKey) continue;
      const raw = localStorage.getItem(k);
      if (!raw || raw.length < 20_000) continue;
      // Inspeccionar plano o descomprimir si es un valor comprimido.
      const sample = raw.startsWith(LZ_PREFIX)
        ? (lzString.decompressFromUTF16(raw.slice(LZ_PREFIX.length)) ?? "")
        : raw;
      if (sample.includes('"data:')) {
        localStorage.removeItem(k);
        evicted += 1;
      }
    }
  } catch {
    /* ignorar */
  }
  return evicted;
}

/**
 * Núcleo de escritura con manejo de cuota compartido por `setStorage` y
 * `setStorageSerialized`. `serialized` es el string FINAL que va a disco
 * (plano JSON o con prefijo `lz1:`); `original` es el valor sin serializar,
 * solo necesario para poder "aligerarlo" en el reintento por cuota llena.
 */
function persistSerializedCore(
  key: string,
  serialized: string,
  original?: unknown,
): void {
  // 1) Camino feliz: el string ya viene serializado, sin re-serializar.
  try {
    localStorage.setItem(key, serialized);
    return;
  } catch (e) {
    if (!isQuotaError(e)) {
      // NUNCA perder en silencio: un fallo distinto de cuota se propaga para
      // que el flujo llamante avise al usuario en vez de dar por guardada la
      // operación. (Antes se hacía `return` y la venta/movimiento se perdía.)
      logger.error("[Mock] Error saving to localStorage", key, e);
      notifyQuotaIssue(key);
      throw e instanceof Error
        ? e
        : new Error(`No se pudo guardar "${key}" en el almacenamiento local`);
    }
  }

  logger.error("[Mock] localStorage lleno al guardar:", key);

  // 2) Reintento aligerando el propio valor (quitar base64 incrustado).
  if (original !== undefined) {
    try {
      let lighter: unknown = original;
      if (Array.isArray(original)) {
        lighter = original.map((item) =>
          item && typeof item === "object" && !Array.isArray(item)
            ? stripBase64FromObject(item as Record<string, unknown>)
            : item,
        );
      } else if (original && typeof original === "object") {
        lighter = stripBase64FromObject(original as Record<string, unknown>);
      }
      localStorage.setItem(key, serializeForStorage(lighter));
      logger.warn("[Mock] Guardado aligerado (sin base64):", key);
      return;
    } catch (e2) {
      if (!isQuotaError(e2)) {
        logger.error("[Mock] Error saving to localStorage (retry)", key, e2);
        notifyQuotaIssue(key);
        throw e2 instanceof Error
          ? e2
          : new Error(`No se pudo guardar "${key}" en el almacenamiento local`);
      }
    }
  }

  // 3) Último recurso: liberar blobs regenerables de otras claves y reintentar.
  const evicted = evictRegenerableBlobs(key);
  if (evicted > 0) {
    try {
      localStorage.setItem(key, serialized);
      logger.warn(
        `[Mock] Guardado tras liberar ${evicted} blob(s) regenerable(s):`,
        key,
      );
      return;
    } catch {
      /* sigue lleno */
    }
  }

  // 4) No se pudo. Avisar y lanzar (NUNCA perder en silencio): el flujo llamante
  //    (ej. runTransaction → processSale) captura el error y avisa al usuario
  //    de forma honesta en lugar de dar la operación por guardada.
  logger.error("[Mock] No se pudo guardar (cuota llena):", key);
  notifyQuotaIssue(key);
  throw new StorageQuotaError(key);
}

/** @deprecated Use txRead/txWrite from @/lib/persistence/transactionalStore instead. */
export const setStorage = <T>(key: string, val: T): void => {
  persistSerializedCore(key, serializeForStorage(val), val);
};

/**
 * Persiste una cadena YA serializada (plano JSON o `lz1:…`) sin re-serializarla.
 * Reutiliza la maquinaria de cuota de `setStorage` (lightening + eviction +
 * `StorageQuotaError`). El espejo IndexedDB guarda la cadena tal cual.
 *
 * Uso interno de la capa transaccional: permite escribir una clave y su journal
 * serializando el valor UNA sola vez (evita dobles JSON.stringify/compresión
 * síncronos por commit, que eran la causa de bloqueos de UI al vender/registrar).
 */
export function setStorageSerialized<T>(
  key: string,
  serialized: string,
  original?: T,
): void {
  persistSerializedCore(key, serialized, original);
}
